import { useLayoutEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card } from 'antd';
import { formatDate } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import {
  useCreateAdmissionSurveyMutation,
  useGetAdmissionSurveyQuery,
} from '@/api/endpoints/schools/schoolSelectionPanel';
import { DataSection, DatePickerField, InputField, SwitchField } from '@/shared/components';
import { DATE_FORMATTERS, ROUTES, TIME_ZONE } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { ESelectionPanelTabs } from '../../../_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '../../../_components/SelectionPanelHeader/SelectionPanelHeader';
import { QuestionsList } from '../../_components/QuestionsList/QuestionsList';

import { CREATE_SURVEY_VALIDATION_SCHEMA } from './consts';
import styles from './SurveyCreatePage.module.scss';
import type { IFormData } from './types';

export const SurveyCreatePage = () => {
  const { id: schoolId } = useParams();
  const navigate = useNavigate();

  const [createSurvey] = useCreateAdmissionSurveyMutation();
  const { isSuccess } = useGetAdmissionSurveyQuery({ schoolId: Number(schoolId) });

  const methods = useForm<IFormData>({
    defaultValues: {
      surveyName: '',
      startDate: undefined,
      endDate: undefined,
      isActive: false,
    },
    resolver: yupResolver(CREATE_SURVEY_VALIDATION_SCHEMA),
  });

  const {
    handleSubmit,
    watch,
    formState: { isValidating },
    trigger,
  } = methods;

  const startDate = watch('startDate');
  watch((_, { name }) => {
    if (name === 'startDate' && isValidating) {
      trigger('endDate');
    }
  });

  const sendApiRequest = useApiRequest();

  const createSurveyHandle = handleSubmit(({ surveyName, startDate, endDate, isActive }) =>
    sendApiRequest({
      apiRequestCallback: () =>
        createSurvey({
          schoolId: Number(schoolId),
          surveyData: {
            title: surveyName,
            start_date: formatDate(startDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY),
            end_date: formatDate(endDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY),
            active: isActive,
          },
        }),
      successMessage: 'Опрос успешно создан',
      successCallback: () => {
        navigate(
          `/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.SELECTION_PANEL}/${ROUTES.ADMISSION_SURVEY}/${ROUTES.VIEW}`,
        );
      },
    }),
  );

  useLayoutEffect(() => {
    if (isSuccess) {
      navigate(
        `/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.SELECTION_PANEL}/${ROUTES.ADMISSION_SURVEY}/${ROUTES.EDIT}`,
      );
    }
  }, [isSuccess]);

  return (
    <div className={styles.wrapper}>
      <SelectionPanelHeader currentTab={ESelectionPanelTabs.AdmissionSurvey} />
      <div className={styles['page-info-wrapper']}>
        <p className={styles.title}>Создать новый опрос</p>
        <p className={styles.description}>Настройте опрос для сбора заявок от кандидатов</p>
      </div>
      <div className={styles.annotation}>
        <span className={styles['annotation-bold']}>Важно</span>: Все поля опроса стандартные. После создания вы сможете
        редактировать даты и статус публикации
      </div>
      <FormProvider {...methods}>
        <Card>
          <div className={styles['main-info-card-wrapper']}>
            <p className={styles['card-title']}>Основная информация</p>
            <DataSection
              titleText="Название опроса"
              isTitleRequired
            >
              <InputField
                fieldName="surveyName"
                placeholder="Например: Заявка на обучение 2026"
              />
            </DataSection>
            <div className={styles['survey-switcher-wrapper']}>
              <div className={styles['survey-switcher-info-wrapper']}>
                <p className={styles['survey-switcher-title']}>Опрос активен</p>
                <p className={styles['survey-switcher-description']}>Разрешить прием заявок через этот опрос</p>
              </div>
              <SwitchField fieldName="isActive" />
            </div>
            <div className={styles['half-width-sections-wrapper']}>
              <DataSection
                titleText="Дата начала"
                isTitleRequired
                wrapperClassName={styles['half-width-section']}
              >
                <DatePickerField
                  fieldName="startDate"
                  format={DATE_FORMATTERS.DAY_MONTH_FULL_YEAR}
                  placement="topRight"
                  minDate={formatInTimeZone(new Date(), TIME_ZONE.MOSCOW, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY)}
                />
              </DataSection>
              <DataSection
                titleText="Дата окончания"
                isTitleRequired
                wrapperClassName={styles['half-width-section']}
              >
                <DatePickerField
                  fieldName="endDate"
                  disabled={!startDate}
                  minDate={startDate}
                  format={DATE_FORMATTERS.DAY_MONTH_FULL_YEAR}
                  placement="topRight"
                />
              </DataSection>
            </div>
          </div>
        </Card>
      </FormProvider>
      <Card>
        <div className={styles['main-info-card-wrapper']}>
          <p className={styles['card-title']}>Поля опроса</p>
          <QuestionsList isEditMode />
        </div>
      </Card>
      <div className={styles['button-wrapper']}>
        <Button
          type="primary"
          onClick={createSurveyHandle}
        >
          Создать опрос
        </Button>
      </div>
    </div>
  );
};
